import React, { useState } from "react";
import {
  Calendar, BookOpen, Send, Wallet, Bookmark, CalendarDays, AlertTriangle, X,
  CheckCircle2, Clock, BellRing, User,
} from "lucide-react";
import { C } from "./tokens";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtUGX } from "@/lib/format";

export function TimetableModal({ onClose }: { onClose: () => void }) {
  const periods = [
    { period: 1, time: "8:00 AM", subject: "Mathematics", room: "Room 12" },
    { period: 2, time: "9:00 AM", subject: "Physics", room: "Lab A" },
    { period: 3, time: "10:00 AM", subject: "English", room: "Room 8" },
    { period: 4, time: "11:00 AM", subject: "Break", room: "" },
    { period: 5, time: "11:30 AM", subject: "Chemistry", room: "Lab B" },
  ];
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.blue}10, ${C.teal}10)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.blue }}><Calendar className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Today's Timetable</h3><p className="text-sm" style={{ color: C.textMuted }}>Primary 6A</p></div>
        </div>
      </div>
      <div className="p-6 max-h-[60vh] overflow-y-auto space-y-2.5">
        {periods.map((p, i) => (
          <div key={i} className="flex items-center gap-4 p-3.5 rounded-xl" style={{ background: p.subject === "Break" ? C.orangeLight : C.bg, border: `1px solid ${C.border}` }}>
            <div className="w-16 text-center shrink-0"><p className="text-xs font-bold" style={{ color: C.textMuted }}>P{p.period}</p><p className="text-[10px]" style={{ color: C.textLight }}>{p.time}</p></div>
            <div className="flex-1"><p className="text-sm font-semibold" style={{ color: C.text }}>{p.subject}</p>{p.room && <p className="text-xs" style={{ color: C.textMuted }}>{p.room}</p>}</div>
          </div>
        ))}
      </div>
    </>
  );
}

export function AssignmentsModal({ onClose }: { onClose: () => void }) {
  const assignments = [
    { id: "a1", title: "Trigonometry Problem Set", subject: "Mathematics", due: "Jul 8", status: "pending" },
    { id: "a2", title: "Lab Report: Enzyme Activity", subject: "Biology", due: "Jul 10", status: "in_progress" },
  ];
  const statusColors: Record<string, string> = { pending: C.amber, in_progress: C.blue, submitted: C.green };
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.purple}10, ${C.pink}10)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.purple }}><BookOpen className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Assignments</h3><p className="text-sm" style={{ color: C.textMuted }}>{assignments.length} due</p></div>
        </div>
      </div>
      <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
        {assignments.map((a) => (
          <div key={a.id} className="p-4 rounded-xl" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-semibold" style={{ color: C.text }}>{a.title}</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: `${statusColors[a.status]}15`, color: statusColors[a.status] }}>{a.status.replace("_", " ").toUpperCase()}</span>
            </div>
            <p className="text-xs" style={{ color: C.textMuted }}>{a.subject} · Due {a.due}</p>
          </div>
        ))}
      </div>
    </>
  );
}

export function MessageModal({ onClose }: { onClose: () => void }) {
  const [message, setMessage] = useState("");
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.green}10, ${C.teal}10)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.green }}><Send className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Message Teacher</h3><p className="text-sm" style={{ color: C.textMuted }}>Compose message</p></div>
        </div>
      </div>
      <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
        <div>
          <label className="text-xs font-semibold mb-2 block" style={{ color: C.textMuted }}>Select Teacher</label>
          <div className="flex flex-wrap gap-2">
            <button className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}>Mr. Okello</button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold mb-2 block" style={{ color: C.textMuted }}>Message</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Type your message..." className="w-full rounded-xl border px-4 py-3 text-sm focus:outline-none resize-none" style={{ background: C.bg, borderColor: C.border, color: C.text }} />
        </div>
      </div>
      <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: C.border }}>
        <Button variant="outline" size="sm" className="rounded-xl h-9 text-xs" onClick={onClose}>Cancel</Button>
        <Button size="sm" className="rounded-xl text-white h-9 text-xs" style={{ background: C.accent }} onClick={() => { toast.success("Message sent!"); onClose(); }}><Send className="h-3.5 w-3.5 mr-1.5" />Send</Button>
      </div>
    </>
  );
}

export function WalletModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.amber}10, ${C.orange}15)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.amber }}><Wallet className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Wallet & Fees</h3></div>
        </div>
        <div className="mt-4 flex items-center gap-6">
          <div><p className="text-xs" style={{ color: C.textMuted }}>Balance</p><p className="text-2xl font-bold" style={{ color: C.amber }}>{fmtUGX(45000)}</p></div>
          <div><p className="text-xs" style={{ color: C.textMuted }}>Fees Due</p><p className="text-2xl font-bold" style={{ color: C.red }}>{fmtUGX(450000)}</p></div>
        </div>
      </div>
      <div className="p-6 max-h-[50vh] overflow-y-auto">
        <p className="text-xs font-semibold mb-3" style={{ color: C.textMuted }}>Recent Transactions</p>
        <div className="space-y-2">
          {[{ title: "Lunch", amount: -15000, date: "Today" }, { title: "Top-up", amount: 50000, date: "Yesterday" }].map((t, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl" style={{ background: C.bg }}>
              <div><p className="text-xs font-medium" style={{ color: C.text }}>{t.title}</p><p className="text-[10px]" style={{ color: C.textMuted }}>{t.date}</p></div>
              <p className="text-sm font-bold" style={{ color: t.amount > 0 ? C.green : C.textMid }}>{t.amount > 0 ? "+" : ""}{fmtUGX(t.amount)}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function SavedListingsModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.orange}10, ${C.gold}10)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.orange }}><Bookmark className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Saved Listings</h3><p className="text-sm" style={{ color: C.textMuted }}>Your bookmarked items</p></div>
        </div>
      </div>
      <div className="p-6 text-center"><p className="text-sm" style={{ color: C.textMuted }}>No saved listings yet</p></div>
    </>
  );
}

export function EventsModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.blue}10, ${C.purple}10)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.blue }}><CalendarDays className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Upcoming Events</h3><p className="text-sm" style={{ color: C.textMuted }}>School activities</p></div>
        </div>
      </div>
      <div className="p-6 space-y-2.5">
        {[{ title: "Science Lab Trip", date: "Jul 7, 2025", color: C.orange }, { title: "End of Term Exam", date: "Jul 18, 2025", color: C.gold }].map((ev, i) => (
          <div key={i} className="flex items-center gap-3 p-3.5 rounded-xl" style={{ background: C.bg }}>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold text-white" style={{ background: ev.color }}>{ev.date.split(" ")[1].slice(0, 2)}</div>
            <div><p className="text-xs font-semibold" style={{ color: C.text }}>{ev.title}</p><p className="text-[10px]" style={{ color: C.textMuted }}>{ev.date}</p></div>
          </div>
        ))}
      </div>
    </>
  );
}

export function ReportModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="relative overflow-hidden px-6 py-5" style={{ background: `linear-gradient(135deg, ${C.red}10, ${C.orange}10)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition"><X className="h-4 w-4" style={{ color: C.textMuted }} /></button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.red }}><AlertTriangle className="h-6 w-6 text-white" /></div>
          <div><h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Report Issue</h3><p className="text-sm" style={{ color: C.textMuted }}>Let us know about a problem</p></div>
        </div>
      </div>
      <div className="p-6 text-center"><p className="text-sm" style={{ color: C.textMuted }}>Report form would appear here</p></div>
    </>
  );
}

export function SettingsView({ user, firstName }: { user: any; firstName: string }) {
  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Settings</h2>
        <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>Account preferences</p>
      </div>
      {[
        { title: "Profile", icon: User, items: [["Name", user?.user_metadata?.full_name ?? firstName], ["Email", user?.email ?? "parent@family.com"]] },
        { title: "Notifications", icon: BellRing, items: [["Fee reminders", "Enabled"], ["Transport updates", "SMS + Email"]] },
      ].map(section => (
        <div key={section.title} className="rounded-2xl border overflow-hidden shadow-premium bg-white border-[#E2D6C0]/60">
          <div className="border-b border-[#E2D6C0]/60 px-5 py-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: C.orangeLight }}><section.icon className="h-4 w-4" style={{ color: C.orange }} /></div>
            <p className="text-sm font-bold" style={{ color: C.text }}>{section.title}</p>
          </div>
          <div className="divide-y divide-[#E2D6C0]/60">
            {section.items.map(([label, value]) => (
              <div key={label} className="flex items-center justify-between px-5 py-3.5">
                <p className="text-sm" style={{ color: C.textMid }}>{label}</p>
                <p className="text-sm font-semibold" style={{ color: C.text }}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
